import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Search, Filter, Plus, Globe, Briefcase, Users } from 'lucide-react';
import { fetchCompaniesPaginated, type CompanyFilters, type CompanyListItem } from '@/lib/api';
import { capitalizeFirst } from '@/lib/utils';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const INDUSTRY_OPTIONS = [
  { value: 'all', label: 'All Industries' },
  { value: 'Technology', label: 'Technology' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Retail', label: 'Retail' },
  { value: 'Energy', label: 'Energy' },
  { value: 'Education', label: 'Education' },
  { value: 'Consulting', label: 'Consulting' },
  { value: 'Life Sciences', label: 'Life Sciences' },
  { value: 'Other', label: 'Other' },
];

export function CompaniesPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [industry, setIndustry] = useState('all');
  const pageSize = 10;

  const handleSearchChange = (value: string) => {
    setSearch(value);
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  const filters: CompanyFilters = useMemo(() => ({
    search: debouncedSearch || undefined,
    industry: industry !== 'all' ? industry : undefined,
    page,
    limit: pageSize,
  }), [debouncedSearch, industry, page]);

  const { data, isLoading } = useQuery({
    queryKey: ['companies', 'paginated', filters],
    queryFn: () => fetchCompaniesPaginated(filters),
  });

  const columns: ColumnDef<CompanyListItem>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Company',
        cell: ({ row }) => {
          const company = row.original;
          return (
            <Link
              to={`/companies/${company.id}`}
              className="flex items-center gap-3 hover:text-emerald-500"
            >
              <Avatar className="h-9 w-9">
                <AvatarFallback>{capitalizeFirst(company.name?.[0]) || '?'}</AvatarFallback>
              </Avatar>
              <div>
                <div className="font-medium">{company.name}</div>
                {company.website && (
                  <div className="text-sm text-text-tertiary truncate max-w-[200px]">
                    {company.website.replace(/^https?:\/\//, '')}
                  </div>
                )}
              </div>
            </Link>
          );
        },
      },
      {
        accessorKey: 'industry',
        header: 'Industry',
        cell: ({ row }) => {
          const industry = row.original.industry;
          if (!industry) return <span className="text-text-tertiary">-</span>;
          return <Badge variant="secondary">{industry}</Badge>;
        },
      },
      {
        accessorKey: 'website',
        header: 'Website',
        cell: ({ row }) => {
          const website = row.original.website;
          if (!website) return <span className="text-text-tertiary">-</span>;
          return (
            <a
              href={website.startsWith('http') ? website : `https://${website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-emerald-500 hover:text-emerald-400"
              onClick={(e) => e.stopPropagation()}
            >
              <Globe className="h-3.5 w-3.5" />
              Visit
            </a>
          );
        },
      },
      {
        accessorKey: 'jobCount',
        header: 'Jobs',
        cell: ({ row }) => {
          const count = row.original.jobCount;
          return (
            <div className="flex items-center gap-1 text-sm">
              <Briefcase className="h-3.5 w-3.5 text-emerald-400" />
              {count}
            </div>
          );
        },
      },
      {
        accessorKey: 'contactCount',
        header: 'Contacts',
        cell: ({ row }) => {
          const count = row.original.contactCount;
          return (
            <div className="flex items-center gap-1 text-sm">
              <Users className="h-3.5 w-3.5 text-emerald-400" />
              {count}
            </div>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => {
          return (
            <span className="text-sm text-text-tertiary">
              {new Date(row.original.createdAt).toLocaleDateString()}
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Companies</h1>
          <p className="text-text-secondary mt-1">
            Manage client companies and contacts
          </p>
        </div>
        <Button asChild>
          <Link to="/companies/new">
            <Plus className="h-4 w-4 mr-2" />
            Add Company
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" />
            <Input
              placeholder="Search by name or industry..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-text-tertiary" />
            </div>
            <Select
              value={industry}
              onValueChange={(value) => {
                setIndustry(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Industry" />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen">
        <DataTable
          columns={columns}
          data={data?.data ?? []}
          page={page}
          pageSize={pageSize}
          total={data?.pagination.total ?? 0}
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="No companies found. Try adjusting your filters."
          getRowId={(row) => row.id}
        />
      </div>
    </div>
  );
}
